import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import SEO from "../../components/SEO";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Login failed");
      return;
    }
    navigate("/admin", { replace: true });
  };

  return (
    <>
      <SEO title="Admin · Ridgehawk Logistics" path="/admin/login" />
      <div className="flex min-h-screen items-center justify-center bg-ridge-charcoal px-6 py-16" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3">
            <img src="/logo-icon.png" alt="" className="h-10 w-10 rounded bg-white p-0.5" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">RIDGEHAWK · ADMIN</span>
          </div>
          <form
            onSubmit={onSubmit}
            className="rounded-md border border-white/10 bg-white p-7 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.5)] md:p-8"
            data-testid="admin-login-form"
            noValidate
          >
            <h1 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
              Sign in
            </h1>
            <p className="mt-2 text-sm text-ridge-muted">
              Admin access for Ridgehawk Logistics.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-login-email"
                  className="block w-full rounded-md border border-ridge-border bg-white px-3.5 py-3 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15 md:text-base"
                />
              </div>
              <div>
                <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                  className="block w-full rounded-md border border-ridge-border bg-white px-3.5 py-3 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15 md:text-base"
                />
              </div>
            </div>

            {error ? (
              <p
                className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                role="alert"
                data-testid="admin-login-error"
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              className="btn-accent mt-6 w-full"
              disabled={submitting}
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" /> Sign in
                </>
              )}
            </button>

            <p className="mt-5 text-center text-sm">
              <Link
                to="/admin/forgot-password"
                className="font-semibold text-ridge-amber transition-colors hover:text-ridge-amber-dark"
                data-testid="admin-login-forgot-link"
              >
                Forgot password?
              </Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
